import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Organization } from '@starter/shared';
import { CreatePlatformOrganizationInputSchema } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { AccessibleModal } from '../../components/AccessibleModal.js';
import { PageHeader } from '../../components/PageHeader.js';

export function PlatformOrganizationsPage() {
  const [rows, setRows] = useState<Organization[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .platformListOrganizations({ page, pageSize: 10, search: search || undefined })
      .then((res) => {
        setRows(res.data);
        setTotalPages(res.totalPages);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load organizations'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [page, search]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setCreateSuccess(null);
    const parsed = CreatePlatformOrganizationInputSchema.safeParse({ name: createName });
    if (!parsed.success) {
      setCreateError(parsed.error.issues[0]?.message ?? 'Invalid name');
      return;
    }
    setCreating(true);
    try {
      const org = await api.platformCreateOrganization(parsed.data);
      setCreateSuccess(`Created ${org.name}`);
      setCreateName('');
      setCreateOpen(false);
      load();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Organizations"
        subtitle="Tenant colleges and training providers"
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setCreateOpen(true)}>
            Create organization
          </button>
        }
      />

      <form
        className="platform-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setSearch(searchInput.trim());
        }}
      >
        <label className="field field--inline">
          <span className="field__label">Search</span>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Organization name"
          />
        </label>
        <button type="submit" className="btn btn--secondary">Search</button>
      </form>

      <AccessibleModal open={createOpen} onClose={() => setCreateOpen(false)} labelledBy="create-org-title">
        <h2 id="create-org-title">Create organization</h2>
        <form onSubmit={handleCreate}>
          <label className="field">
            <span className="field__label">Organization name</span>
            <input value={createName} onChange={(e) => setCreateName(e.target.value)} disabled={creating} />
          </label>
          {createError && <div className="banner banner--error" role="alert">{createError}</div>}
          <div className="modal__actions">
            <button type="button" className="btn btn--ghost" onClick={() => setCreateOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={creating}>
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </AccessibleModal>

      {createSuccess && <div className="banner banner--success" role="status">{createSuccess}</div>}
      {loading ? <LoadingState message="Loading organizations…" /> : null}
      {error ? <ErrorBanner message={error} onRetry={load} /> : null}

      {!loading && !error && (
        <>
          {rows.length === 0 ? (
            <p className="empty-state">No organizations match your search.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((org) => (
                    <tr key={org.id}>
                      <td>{org.name}</td>
                      <td>
                        <span className={`status-pill status-pill--${org.status}`}>{org.status}</span>
                      </td>
                      <td>{new Date(org.createdAt).toLocaleDateString()}</td>
                      <td className="actions-cell">
                        <Link
                          to={`/platform/organizations/${org.id}`}
                          className="btn btn--ghost btn--sm"
                          aria-label={`View ${org.name}`}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="pagination">
              <button type="button" className="btn btn--ghost btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button type="button" className="btn btn--ghost btn--sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
